import React, { useEffect, useMemo, useState } from "react";
import DashboardLayout from "@layouts/dashboard/dashboard-layout";
import { getValue } from "@utils/lodash";
import { NavLink } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import NavigationBar from "@components/NotificationBar/index";
import DeleteModal from "@_common/deleteModal";
import { toast } from "react-toastify";
import ScrollableReactTable from "@components/ScrollableDatatable";
import PaginationPage from "@components/pagination/pagination";
import SearchHeader from "@components/common/SearchHeader";
import {
  adminDeleteSpecificOrganization,
  adminGetSpecificOrganization,
} from "@services/client-list.service";

import Notfound from "@components/Not-Found";
import {
  adminBulkUpdateVendorStatus,
  clientGetVendor,
  exportVendorEmailMapping,
  exportVendors,
  exportVendorsUser,
} from "@services/vendors.service";
import Loader from "@components/common/Loader";
import { QueryRequestHelper, QueryRequestHelperShowFalse } from "@_common/query-request-helper";
import { Button } from "reactstrap";
import NoAccessPage from "@components/common/NoAccess";
import { handleDownloadOnClick } from "@_common/downloadfile";
import { currentDate } from "@_common/date-helpers";
import {
  adminGetCitiesListManagement,
  adminGetCountriesListManagement,
  adminGetStatesListManagement,
} from "@services/list-management.service";
import FilterForm from "@components/common/AddFilter/FilterForm";
import ConfigVendorsNotfound from "@components/Page-Not-Found/config-vendors-not-found";
import MoreConfigSvgComponent from "../../assets/svg-components/left-menu/more-config";
import RightMenuHeaders from "@components/common/RightMenuHeaders";
import { countKeysWithValues } from "@_common/text-helpers";
import SettingViewSubheaderSvgComponent from "../../assets/svg-components/sub-header/setting";
import ReactTooltip from "react-tooltip";
import { UpdateVendorSetting } from "@services/self-compliance.service";
import BulkDownload from "@components/BulkUploadDropdown/bulk-download";

import DownloadSubheaderSvgComponent from "../../assets/svg-components/sub-header/download";
import { allParams, allParamsExceptSearch } from "@_common/common-helper";

interface ILocationsProps {}

const Vendors: React.FunctionComponent<ILocationsProps> = (props: any) => {
  let navigate = useNavigate();

  /* -------------------------------------------------------------------------- */
  /*                               UseState Section                             */
  /* -------------------------------------------------------------------------- */

  const [isLoading, setIsLoading] = React.useState(true);
  const [list, setList] = React.useState<[]>([]);
  const [dropdownOptions] = useState([
    { value: "VENDOR", label: "Vendor" },
    { value: "VENDOR_USER", label: "User" },
    { value: "MAP_VENDOR_LOCATION", label: "Map to Vendor Location" },
    { value: "VENDOR_HEADCOUNT", label: "Headcount" },
    { value: "LOCATION_MAPPING", label: "Location Mapping to User" },
    { value: "MAP_VENDOR_LOCATION_EMAIL", label: "Location Email Mapping" },
    // { value: "LOCATION_MAPPING", label: "Location Mapping to User" },
  ]);
  const [filterIsOpen, setFilterIsOpen] = useState(false);
  const handleFilter = () => {
    setFilterIsOpen(!filterIsOpen);
  };
  const [loading, setLoading] = useState(true);

  /* -------------------------------------------------------------------------- */
  /*                               UseEffect Section                            */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if (Object.keys(params).length === 0 && props.permissionAPITriggered) {
      getData();
      getCountriesList();
    }
  }, [props.permissionAPITriggered]);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if (Object.keys(params).length > 0 && props.permissionAPITriggered) {
      getData();
      getCountriesList();

      if (params.page_no) {
        setPage_no(parseInt(params.page_no));
      }
      if (params.search_text) {
        setSearchInput(params.search_text);
        setShowSearchTextbox(true);
      }
      if (params.limit) {
        setLimit(parseInt(params.limit));
      }

      setRequest({
        ...request,
        country_id: getValue(params, `country_id`, ""),
        state_id: getValue(params, `state_id`, ""),
        city_id: getValue(params, `city_id`, ""),
      });

      if (getValue(params, `country_id`, "")) {
        setFilterVisibility(true);
        getStatesList(getValue(params, `country_id`, ""));
      }
    }
  }, [window.location.href, props.permissionAPITriggered]);

  useEffect(() => {
    if (props.permissionAPITriggered) {
      let user = JSON.parse(getValue(localStorage, `user`, ""));
      if (getValue(user, `org_id`, "") && props.permissionAPITriggered) {
        getVCData(getValue(user, `org_id`, ""));
      } else {
        setLoading(false);
      }
    }
  }, [props.permissionAPITriggered]);

  /* -------------------------------------------------------------------------- */
  /*                               API Section                                  */
  /* -------------------------------------------------------------------------- */
  const getVCData = async (id: string) => {
    try {
      setIsLoading(true);
      let resp = await adminGetSpecificOrganization(id);
      if (resp) {
        setIsLoading(false);
        setVCRequest({
          ...vcRequest,
          required_pending_status: getValue(
            resp,
            `data.data.required_pending_status`,
            false
          ),
        });
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };
  /* -------------------------------------------------------------------------- */
  /*                               API Section                                  */
  /* -------------------------------------------------------------------------- */

  const getData = async () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());

    let payload: any = {
      page_no: params.page_no ? parseInt(params.page_no) : 1,
      page_size: params.limit ? parseInt(params.limit) : limit,
      search_text: params.search_text,
      sort_by: params.sort_by,
      vendor_type: params.vendor_type ? params.vendor_type : "SERVICING_VENDOR",
      // is_default: true,
      is_active: params.status === "active" ? true : params.status === "inactive" ? false : true,
      country_id: getValue(params, `country_id`, ""),
      state_id: getValue(params, `state_id`, ""),
      city_id: getValue(params, `city_id`, ""),
    };
    let queryRequest = QueryRequestHelperShowFalse(payload);

    try {
      setIsLoading(true);
      let resp = await clientGetVendor(queryRequest);
      if (resp) {
        setList(
          getValue(resp, `data.vendors`, []).map((item: object) => ({
            ...item,
            // created_at: moment(getValue(item, `created_at`, "")).format(
            // 	"DD MMM YYYY"
            // ),
            status: getValue(item, `is_active`, false) ? "Active" : "Inactive",
            city_name: getValue(item, `city.name`, ""),
            industry: getValue(item, `industry.text`, ""),
            business_type: getValue(item, `business_type.text`, ""),
          }))
        );
        setTotalCount(getValue(resp, `data.pagination.total`, 0));
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                               Search section                               */
  /* -------------------------------------------------------------------------- */
  const [showSearchTextbox, setShowSearchTextbox] = useState(false);
  const [search_text, setSearchInput] = useState<any>("");

  const toggleSearch = () => {
    setShowSearchTextbox((prevState) => !prevState);
  };
  const toggleSearchClose = () => {
    setSearchInput("");
    toggleSearch();
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if (params.search_text) {
      delete params.search_text;
      let payload: any = { ...params };
      let queryRequest = QueryRequestHelper(payload);
      navigate(`${window.location.pathname}?${queryRequest}`);
      getData();
    }
  };

  const handleChangeSearch = async (e: any) => {
    setSearchInput(e.target.value);
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let payload: any = {
      ...params,
      page_no: 1,
      search_text: e.target.value,
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
    if (!e.target.value) {
      getData();
    }
  };

  const [selectedIDs, setSelectedIDs] = useState<any>([]);
  const getTableState = (value: object) => {
    let selectedIndexList = Object.keys(
      getValue(value, `selectedRowIds`, {})
    ).map((item) => parseInt(item));
    let selectedIds =
      list.length > 0
        ? list.filter((_item: object, index: number) =>
            selectedIndexList.includes(index)
          )
        : [];
    if (selectedIds.length > 0) {
      setSelectedIDs(
        selectedIds.map((item: object) => getValue(item, `id`, ""))
      );
    } else {
      setSelectedIDs([]);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                                 Delete section                             */
  /* -------------------------------------------------------------------------- */

  const [deleteValue, setDeleteValue] = useState("");
  const [deleteId, setDeleteId] = useState("");
  const handleDeleteFun = (id: string, value: string) => {
    setDeleteValue(value);
    setDeleteId(id);
    handleDelete();
  };
  const [isDelete, setIsDelete] = useState(false);
  const handleDelete = () => {
    setIsDelete(!isDelete);
  };

  const handleDeleteFunction = async () => {
    try {
      let resp = await adminDeleteSpecificOrganization(deleteId);
      if (resp) {
        toast.success(getValue(resp, `message`, ""));
        handleDelete();
        getData();
      }
    } catch (error) {}
  };

  /* -------------------------------------------------------------------------- */
  /*                               Onchange section                             */
  /* -------------------------------------------------------------------------- */

  const handleChangeSortBy = (value: string) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    params.sort_by = value;
    props.history.push({
      pathname: window.location.pathname,
      query: params,
    });
  };

  const handleChangeStatus = (value: string) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    params.status = value;
    props.history.push({
      pathname: window.location.pathname,
      query: params,
    });
  };
  /* -------------------------------------------------------------------------- */
  /*                               Pagination section                           */
  /* -------------------------------------------------------------------------- */

  const [totalCount, setTotalCount] = useState(0);
  const [limit, setLimit] = useState(100);
  const [page_no, setPage_no] = useState(1);

  const handleChangePagination = (page_no: string) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let payload = {
      ...params,
      page_no: page_no,
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
  };

  const handleChangeLimit = (limit: string) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let payload: any = {
      ...params,
      limit: limit,
      page_no: "1",
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
  };

  const handleEdit = (e: any, obj: object) => {
    e.stopPropagation();
    navigate(
      `/configuration/vendors/detail-info/profile/${getValue(
        obj,
        `parent_id`,
        ""
      )}/${getValue(obj, `id`, "")}`
    );
  };
  const rowClick = (obj: object) => {
    // console.log(getValue(obj,`original`,''))
    // navigate(
    //   `/configuration/vendors/detail/${getValue(
    //     obj,
    //     `original.parent_id`,
    //     ""
    //   )}?${mainQueryRequest}`
    // );
    navigate(
      `/configuration/vendors/detail-info/profile/${getValue(
        obj,
        `original.parent_id`,
        ""
      )}/${getValue(obj, `original.id`, "")}?${allParamsExceptSearch()}
      `
    );
  };
  const getTrProps = (rowInfo: any) => {
    if (getValue(rowInfo, `id`, "")) {
      return {
        style: {
          //   background:  'red',
          //   color: 'black'
        },
      };
    }
    return {};
  };
  const columns = React.useMemo(
    () => [
      {
        Header: "Vendor ID",
        accessor: "vendor_code",
        width: 100,
        Cell: ({ row }: any) => {
          return (
            <NavLink
              to={`/configuration/vendors/detail/${row.original.parent_id}?${mainQueryRequest}`}
            >
              {row.values.vendor_code}
            </NavLink>
          );
        },
      },
      {
        Header: "Vendor Name",
        accessor: "vendor_name",
        width: 200,
      },
      {
        Header: "Industry",
        accessor: "industry",
        width: 150,
      },
      {
        Header: "Corporate Location",
        accessor: "city_name",
        width: 180,
      },
      {
        Header: "Business Type",
        accessor: "business_type",
        width: 180,
      },
      {
        Header: "Status",
        accessor: "status",
        width: 100,
      },
      {
        Header: "Action",
        width: 100,
        Cell: ({ row }: any) => {
          return (
            <>
              <button
                color="primary"
                className="header-apply-btn"
                onClick={(e) => handleEdit(e, row.original)}
              >
                Edit
              </button>
            </>
          );
        },
      },
    ],
    []
  );

  const handleChangeActiveStatus = async (value: string) => {
    try {
      let resp = await adminBulkUpdateVendorStatus({
        ids: selectedIDs,
        is_active: value === "active" ? true : false,
      });
      if (resp) {
        toast.success(getValue(resp, `message`, ""));
        setSelectedIDs([]);
        getData();
      } else {
        getData();
      }
    } catch (error) {}
  };

  const urlSearchParams = new URLSearchParams(window.location.search);
  const params = Object.fromEntries(urlSearchParams.entries());
  const [check, setCheck] = useState(false);
  const handleRemoveNavItems = () => {
    setCheck(!check);
    setSelectedIDs([]);
  };
  /* -------------------------------------------------------------------------- */
  /*                             Download Reports                               */
  /* -------------------------------------------------------------------------- */
  const [downloadLoading, setDownloadLoading] = useState(false);
  const handleDownloadSelfComplianceReport = async () => {
    let payload: any = {
      report_type: "VENDOR",
      report_name: `vendor_report_${currentDate()}`,
      country_id: getValue(request, `country_id`, ""),
      state_id: getValue(request, `state_id`, ""),
      city_id: getValue(request, `city_id`, ""),
      is_active: params.status === "active" ? true : params.status === "inactive" ? false : true,
    };
    Object.keys(payload).forEach((key) => {
      const value = payload[key];
      if (value === undefined || value === null || value === "") {
        delete payload[key];
      }
    });
        setDownloadLoading(true);
    try {
      let resp = await exportVendors({
        ...payload,
      });
      if (resp) {
        setDownloadLoading(false);
        toast.success(getValue(resp, `message`, ""));
        navigate(`/self-compliance-logs/VENDOR`);
      } else {
        setDownloadLoading(false);
      }
    } catch (error) {
      setDownloadLoading(false);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                             Download Reports                               */
  /* -------------------------------------------------------------------------- */
  const handleDownloadVendorUserReport = async () => {
    let payload: any = {
      report_type: "VENDOR_USER_REPORT",
      report_name: `vendor_user_report_${currentDate()}`,
      // from_date: "2024-11-01",
      // to_date: "2024-11-01",
    };
    Object.keys(payload).forEach((key) => {
      if (!payload[key] || payload[key] === undefined) {
        delete payload[key];
      }
    });
    setDownloadLoading(true);
    try {
      let resp = await exportVendorsUser({
        ...payload,
      });
      if (resp) {
        setDownloadLoading(false);
        toast.success(getValue(resp, `message`, ""));
        navigate(`/self-compliance-logs/VENDOR_USER_REPORT`);
      } else {
        setDownloadLoading(false);
      }
    } catch (error) {
      setDownloadLoading(false);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                             Email Mapping Reports                          */
  /* -------------------------------------------------------------------------- */
  const handleDownloadVendorEmailMappingReport = async () => {

    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());

    let payload: any = {
      page_no: params.page_no ? parseInt(params.page_no) : 1,
      page_size: params.limit ? parseInt(params.limit) : limit,
      search_text: params.search_text,
    };

    let queryRequest = QueryRequestHelper(payload);
    try {
      let resp = await exportVendorEmailMapping(queryRequest);
      if (resp) {
        let URL = getValue(resp, `data.exportResult.downloadUrl`, "");
        if (URL) {
          handleDownloadOnClick(URL, "email_mapping_report");
        }
      }
    } catch (error) {}

  };

  /* -------------------------------------------------------------------------- */
  /*                        smart Fileter section                               */
  /* -------------------------------------------------------------------------- */
  const [request, setRequest] = useState<any>({
    country_id: "101",
    state_id: "",
    city_id: 0,
    status: ""
  });

  const [isFilterVisible, setFilterVisibility] = React.useState(false);

  useEffect(() => {
    if (request.country_id) {
      getStatesList(request.country_id);
    }
    if (request.state_id) {
      getCitiesList(request.state_id);
    }
  }, [request.country_id, request.state_id]);

  const [countryList, setCountryList] = useState([]);
  const getCountriesList = async () => {
    try {
      let resp = await adminGetCountriesListManagement();
      if (resp) {
        setCountryList(
          getValue(resp, `data.countries`, []).map((item: object) => ({
            ...item,
            value: getValue(item, `name`, ""),
            label: getValue(item, `name`, ""),
          }))
        );
      } else {
      }
    } catch (error) {}
  };
  const [states, setStates] = useState([]);
  const getStatesList = async (countryId: number) => {
    if (countryId) {
      try {
        let resp = await adminGetStatesListManagement(countryId);
        if (resp) {
          setStates(
            getValue(resp, `data.states`, []).map((item: object) => ({
              ...item,
              value: getValue(item, `name`, ""),
              label: getValue(item, `name`, ""),
            }))
          );
        } else {
        }
      } catch (error) {}
    }
  };
  const [citiesList, setCityList] = useState([]);
  const getCitiesList = async (stateId: number) => {
    if (stateId) {
      try {
        let resp = await adminGetCitiesListManagement(stateId);
        if (resp) {
          // console.log(resp);
          setCityList(
            getValue(resp, `data.cities`, []).map((item: object) => ({
              ...item,
              value: getValue(item, `name`, ""),
              label: getValue(item, `name`, ""),
            }))
          );
        } else {
        }
      } catch (error) {}
    } else {
      setCityList([]);
    }
  };

  const reset = () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let payload: any = {
      page_no: params.page_no,
      search_text: getValue(params, `search_text`, ""),
      limit: getValue(params, `limit`, ""),
    };
    setRequest({
      ...request,
      country_id: getValue(request, `country_id`, ""),
      state_id: "",
      city_id: "",
      status: "",
    });
    setFilterVisibility(false);
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
    getData();
  };

  const handleChangeSelect = (e: any, type: string) => {
    if (type === "country_id") {
      getStatesList(getValue(e, `id`, ""));
      setRequest({
        ...request,
        country_id: getValue(e, `id`, ""),
      });
    }
    if (type === "state_id") {
      getCitiesList(getValue(e, `id`, ""));
      setRequest({
        ...request,
        state_id: getValue(e, `id`, ""),
      });
    }
    if (type === "city_id") {
      setRequest({
        ...request,
        city_id: getValue(e, `id`, ""),
      });
    }
  };

  const handleSubmitSmartFilter = () => {
    if (
      getValue(request, `country_id`, "")
      // getValue(request, `state_id`, "")
      // && getValue(request, `category_of_law_id`, "") &&
      // getValue(request, `legislation_id`, "") &&
      // getValue(request, `legislation_rule_id`, "")
    ) {
      const urlSearchParams = new URLSearchParams(window.location.search);
      const params = Object.fromEntries(urlSearchParams.entries());
      let payload: any = {
        ...params,
        page_no: params.page_no,
        country_id: getValue(request, `country_id`, ""),
        state_id: getValue(request, `state_id`, ""),
        city_id: getValue(request, `city_id`, ""),
        status: getValue(request, `status`, ""),
      };
      let queryRequest = QueryRequestHelper(payload);
      navigate(`${window.location.pathname}?${queryRequest}`);
      handleFilter();
    } else {
      toast.error("All fields are mandatory");
    }
  };
  let country_ID =
    getValue(countryList, `length`, 0) > 0 &&
    countryList.filter(
      (item: object) =>
        getValue(item, `id`, "") == getValue(request, `country_id`, "")
    );
  let state_ID =
    getValue(states, `length`, 0) > 0 &&
    states.filter(
      (item: object) =>
        getValue(item, `id`, "") == getValue(request, `state_id`, "")
    );

  let city_ID =
    getValue(citiesList, `length`, 0) > 0 &&
    citiesList.filter(
      (item: object) =>
        getValue(item, `id`, "") == getValue(request, `city_id`, "")
    );
  const urlParamsFun = () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let request = QueryRequestHelper({ ...params });
    return request;
  };

  const mainQueryRequest = useMemo(
    () => urlParamsFun(),
    [window.location.href]
  );

  // const [showFilterPopup, setShowFilterPopup] = useState(false);
  // setShowFilterPopup(!showFilterPopup);
  const handleNavigateLogs = () => {
    navigate(`/file-logs/VENDOR`);
  };

  const [showBlogsPopup, setShowBlogsPopup] = useState(false);
  const handleViewBlogs = async () => {
    setShowBlogsPopup(true);
  };

  const [vcRequest, setVCRequest] = useState({
    // email_enable: false,
    required_pending_status: false,
  });

  const [submitLoading, setSubmitLoading] = useState(false);

  const handleSubmitConfigVendor = async () => {
    try {
      setSubmitLoading(true);
      let payload: any = {
        required_pending_status: getValue(
          vcRequest,
          `required_pending_status`,
          false
        ),
      };
      let resp = await UpdateVendorSetting(payload);
      if (resp) {
        toast.success(getValue(resp, `message`, ""));
        setSubmitLoading(false);
        let user = JSON.parse(getValue(localStorage, `user`, ""));
        if (getValue(user, `org_id`, "") && props.permissionAPITriggered) {
          getVCData(getValue(user, `org_id`, ""));
        }
        setShowBlogsPopup(false);
      } else {
        setSubmitLoading(false);
      }
    } catch (error) {}
  };

   const handleChangeSelectByDirectValue = (value: string, name: string) => {
    setRequest({
      ...request,
      [name]: value ? value : "",
    });
  };

  return (
    <DashboardLayout
      permissions={getValue(props, `tabPermissionList`, [])}
      subTabPermissionList={getValue(props, `subTabPermissionList`, [])}
    >
      {!isLoading &&
      !getValue(props, `subTabPermissionList`, []).includes(
        "configuration_vendor"
      ) ? (
        <NoAccessPage />
      ) : (
        <>
          {getValue(selectedIDs, `length`, 0) > 0 && (
            <NavigationBar
              selectedIDs={selectedIDs}
              isMarkInactiveSectionVisible={
                getValue(props, `permissions`, []).includes("update")
                  ? true
                  : false
              }
              handleChangeActiveStatus={handleChangeActiveStatus}
              text={"MARK INACTIVE"}
              multi={
                getValue(props, `permissions`, []).includes("update")
                  ? true
                  : false
              }
              handleRemoveNavItems={handleRemoveNavItems}
            />
          )}

          {/* <div className="dashboard-wrapper__header">
            <h4 className="dashbaord-wrapper__header-title">Vendors</h4>
          </div> */}

          <div className="dashboard-wrapper__header d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div className="header-parent-icon-holder">
                <MoreConfigSvgComponent />
              </div>

              <div className="title-wrapper">
                <h6 className="dashbaord-wrapper__header-parent-title">
                  Configuration
                </h6>
                <h4 className="dashbaord-wrapper__header-title">Vendors</h4>
              </div>
            </div>

            <div className="d-flex">
              <RightMenuHeaders
                route={"/configuration/vendors/add"}
                //search
                isSearch={true}
                searchInput={search_text}
                showSearchTextbox={showSearchTextbox}
                toggleSearch={toggleSearch}
                toggleSearchClose={toggleSearchClose}
                handleChangeSearch={handleChangeSearch}
                //smartFilter
                smartFilter={true}
                handleFilter={handleFilter}
                add={
                  getValue(props, `permissions`, []).includes("create")
                    ? "true"
                    : "false"
                }
                commonUpload={
                  getValue(props, `permissions`, []).includes("update")
                    ? true
                    : false
                }
                dropdownOptions={dropdownOptions}
                //download section
                downloadSelfComplianceReport={false}
                // viewDownloadedSelfComplianceReports={true}
                handleDownloadSelfComplianceReport={
                  handleDownloadSelfComplianceReport
                }
                downloadLoading={downloadLoading}
                // viewDownloadedSelfComplianceReports={true}
                // handleNavigateLogs={handleNavigateLogs}
                commonViewLogs
                //Line Seperator of button
                lineSeperator={true}
                selectedFilterCount={countKeysWithValues(params)}
                vendorBulkDownload
                handleDownloadVendorReport={handleDownloadSelfComplianceReport}
                handleDownloadVendorUserReport={handleDownloadVendorUserReport}
                handleDownloadVendorEmailMappingReport={handleDownloadVendorEmailMappingReport}
              />
              
              <button
                className="icons-btn-sub-header"
                data-tip
                data-for="subHeaderSetting"
                onClick={() => {
                  handleViewBlogs();
                }}
              >
                <SettingViewSubheaderSvgComponent />
              </button>
            </div>

            <ReactTooltip id="subHeaderSetting" effect="solid" place="bottom">
              Configure Vendor
            </ReactTooltip>
          </div>

          <div
            className={`main-page-layout-wrapper`}
            id={
              getValue(selectedIDs, `length`, 0) > 0
                ? "blueNavigationVisible"
                : ""
            }
          >
            <div className="admin-wrapper position-relative">
              <div className="admin-wrapper position-relative d-none">
                <SearchHeader
                  route={"/configuration/vendors/add"}
                  searchInput={search_text}
                  showSearchTextbox={showSearchTextbox}
                  toggleSearch={toggleSearch}
                  toggleSearchClose={toggleSearchClose}
                  handleChangeSearch={handleChangeSearch}
                  // isBulkUploadVisible={
                  // 	getValue(props, `permissions`, []).includes("update")
                  // 		? true
                  // 		: false
                  // }
                  smartFilter={true}
                  add={
                    getValue(props, `permissions`, []).includes("create")
                      ? "true"
                      : "false"
                  }
                  commonUpload={
                    getValue(props, `permissions`, []).includes("update")
                      ? true
                      : false
                  }
                  dropdownOptions={dropdownOptions}
                  //download section
                  downloadSelfComplianceReport={true}
                  // viewDownloadedSelfComplianceReports={true}
                  handleDownloadSelfComplianceReport={
                    handleDownloadSelfComplianceReport
                  }
                  downloadLoading={downloadLoading}
                  setFilterFormVisibility={() => {
                    setFilterVisibility(!isFilterVisible);
                  }}
                  isFilterVisible={isFilterVisible}
                  filterIsOpen={filterIsOpen}
                  handleFilter={() => {
                    setFilterIsOpen(!filterIsOpen);
                  }}
                />
              </div>

              {/* {isFilterVisible && ( */}
              <FilterForm
                setFilterFormVisibility={() => {
                  setFilterVisibility(!isFilterVisible);
                }}
                request={request}

                // showFilterPopup={showFilterPopup}
                // setShowFilterPopup={setShowFilterPopup}
                countryList={countryList}
                states={states}
                citiesList={citiesList}
                handleChangeSelect={handleChangeSelect}
                reset={reset}
                handleSubmitSmartFilter={handleSubmitSmartFilter}
                events={false}
                location={true}
                vendor={true}
                additionalFilter={false}
                country_ID={country_ID}
                state_ID={state_ID}
                city_ID={city_ID}
                filterIsOpen={filterIsOpen}
                handleFilter={handleFilter}
                hideActive
                handleChangeSelectByDirectValue={
                  handleChangeSelectByDirectValue
                }
              />
              {/* )} */}
              {isLoading || !props.permissionAPITriggered ? (
                <Loader />
              ) : list.length > 0 ? (
                <ScrollableReactTable
                  columns={columns}
                  data={list}
                  getTableState={getTableState}
                  rowClick={rowClick}
                  check={check}
                  selectedIDs={selectedIDs}
                  getTrProps={getTrProps}
                />
              ) : (
                <>
                  {Object.keys(params).length !== 0 ? (
                    <>
                      <ConfigVendorsNotfound />
                    </>
                  ) : (
                    <ConfigVendorsNotfound />
                  )}
                </>
              )}
            </div>
            <div className="footer-wrapper d-flex justify-content-between">
              {list.length > 0 && (
                <img
                  src="/images/building-illustration.svg"
                  className="img-fluid"
                />
              )}
              {totalCount > 10 ? (
                <PaginationPage
                  totalCount={totalCount}
                  limit={limit}
                  pageNumber={page_no}
                  handleChangePagination={handleChangePagination}
                  handleChangeLimit={handleChangeLimit}
                />
              ) : (
                <p className="show-rows-count mt-3 d-flex">
                  Total Count:&nbsp;&nbsp;{" "}
                  <p style={{ color: "rgb(13, 110, 253)" }}>{totalCount}</p>
                </p>
              )}
            </div>
            <DeleteModal
              isOpen={isDelete}
              handleModal={handleDelete}
              handleSubmit={handleDeleteFunction}
              deleteValue={deleteValue}
            />

            <div
              className={`compelte-task-wrapper compelte-task-wrapper-add vendor-setting-popup ${
                showBlogsPopup ? "compelte-task-wrapper--active" : ""
              }`}
              style={{ maxWidth: "50%" }}
            >
              <div className="compelte-task-wrapper__header d-flex align-items-center justify-content-between">
                <h6 className="compelte-task-wrapper__title">
                  Configure Vendor{" "}
                </h6>
                <img
                  src="/images/close-white.svg"
                  className="img-fluid navigation-bar-wrapper__close-btn"
                  onClick={() => {
                    setShowBlogsPopup(false);
                  }}
                />
              </div>

              <div className="p-4">
                <div className="row">
                  <div className="col-md-12 mb-2">
                    <div className="rn-reminder-wrap d-flex align-items-center">
                      <div
                        className="custom-switch-wrapper-blue custom-switch-wrapper-icons"
                        style={{ marginTop: "5px" }}
                      >
                        <label className="switch">
                          <input
                            type="checkbox"
                            name="required_pending_status"
                            checked={getValue(
                              vcRequest,
                              `required_pending_status`,
                              false
                            )}
                            onChange={() =>
                              setVCRequest({
                                ...vcRequest,
                                required_pending_status: !getValue(
                                  vcRequest,
                                  `required_pending_status`,
                                  false
                                ),
                              })
                            }
                          />
                          <span className="slider round"></span>
                        </label>
                      </div>
                      <label className="rn-vendor-label-wrapper mb-0 ms-2">
                        Enable Checkpoints to move to "<b>Pending Audit</b>"
                        upon release date expiry
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              <div className="compelte-task-wrapper__cta d-flex align-items-center px-4 py-2 border-top">
                <button
                  className="ascent-button ascent-button--primary"
                  onClick={() => setShowBlogsPopup(false)}
                >
                  Close
                </button>

                <button
                  className="ascent-button ascent-button--secondary"
                  onClick={() => handleSubmitConfigVendor()}
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </DashboardLayout>
  );
};

export default Vendors;
